/***********************/
/* Angular + external */
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from "@angular/core";
import { LoggerService } from "@bfx/logger";
import { TranslateService } from "@ngx-translate/core";
import { CustomField, CustomValue, CustomValueImpl } from "shared/cubiq-model-multi-asset";
/***********************/
import { ATDLNControlType } from "src/app/order-entry/data-atdl/enums/atdl-control-type.enum";
import { ICNInputControlState } from "./models/bfx-interface-controls.enums";
import { BfxAutoCompleteComponent } from "app/bfx-element-library/bfx-autocomplete.component";

const logger = LoggerService.for(__dirname);

export type DomainValue = {
  id: number;
  label: string;
};

export type CustomValueUpdate = {
  defn: CustomField;
  value: CustomValue;
  controlState: ICNInputControlState;
};

/**
 * ICCustomFieldsComponent
 * -----------------------
 * Component that takes a a single CustomFieldInstance bind to a corresponding control that allow the values to be displayed, changed and validated.
 * THe value is represented internally as a string, for free text fields or as a Union of DomainValue (for found domain values) or strings (for
 * search input).
 */

@Component({
  selector: "ic-custom-field-instance",
  templateUrl: "./html/ic-custom-field-instance.html",
  standalone: false,
})
export class ICCustomFieldInstanceComponent implements OnInit, OnDestroy {
  // Hoist enum for template
  public ATDLNControlType = ATDLNControlType;

  private _fieldType: ATDLNControlType;

  /** Entered value - will be a string if free text, or domain where search text has been typed */
  private _value: DomainValue | string = null;

  /** Has validation failed? */
  public hasError: boolean;

  /** Error message displayed in the tooltip  */
  @Input()
  public errorMsg: string;

  public domainValues: Array<DomainValue>;
  public domainSuggestions: Array<DomainValue>;

  private myCustomValue: CustomValueImpl;
  public state: ICNInputControlState = ICNInputControlState.VALID;
  public maxLength: number;

  private _isDisabled: boolean = false;

  @Input() public defn: CustomField;

  @Input()
  private _externalState: ICNInputControlState;

  public get externalState(): ICNInputControlState {
    return this._externalState;
  }

  public set externalState(value: ICNInputControlState) {
    this._externalState = value;

    if (this.externalState == ICNInputControlState.INVALID) {
      this.validate();
    }
  }

  public inputFocused = false;

  private _isInitialized: boolean;
  private _initValue: string;

  /** Will be used when turning off fields based on operation state */
  @Input() hasStrategyFill: boolean = false;

  private _autocompleteConfig: any = {};

  @Output() valueChange = new EventEmitter<CustomValueUpdate>();

  @ViewChildren("autocomplete") autocompleteList: QueryList<BfxAutoCompleteComponent>;

  constructor(protected translate: TranslateService) {}

  private emitValueChange() {
    this.valueChange.emit({
      value: this.myCustomValue,
      defn: this.defn,
      controlState: this.state,
    });
  }

  /**********************************************************************/
  /* LIFE CYCLE HOOKS */
  ngOnInit() {
    this.initialize();
  }

  ngOnDestroy() {
    this.valueChange.complete();
  }

  /**********************************************************************/
  /* INITIALIZATION */
  public initialize(): void {
    this.setError(false);

    if (this.defn) {
      this._autocompleteConfig = {
        allowEmpty: this.defn.isNullableFg,
        forceSelection: !this.defn.isNullableFg,
      };

      this._fieldType = this.defn.freeFormatTxtFg
        ? ATDLNControlType.TEXTFIELD
        : ATDLNControlType.DROPDOWN_LIST;
      this.maxLength = this.defn.maxLen + 1;
      this._value = null;
      this.initDomainValues();
      this.myCustomValue = new CustomValueImpl();
      this.myCustomValue.fieldId = this.defn.fieldId;
      this._isInitialized = true;
      this.primeFromInitValue();
    }
  }

  /**
   * Set the error state and ensure that the component state is updated accordingly taking into
   * account the disabled and inputFocused flags
   *
   * @param value
   */
  private setError(value: boolean): void {
    if (value != this.hasError) {
      this.hasError = value;
      this.updateState();
    }
  }

  /**
   * The configuration for the BFX autocomplete field
   */
  public get autocompleteConfig(): any {
    return this._autocompleteConfig;
  }

  /**
   * Extract all the  possible field values and put them in domainValues array in Object
   * form (which I think is necessary for autocomplete)
   */
  private initDomainValues(): void {
    this.domainValues = [];

    if (this.defn.domainValues) {
      let i = 0;
      for (let value of this.defn.domainValues) {
        this.domainValues.push({ id: i++, label: value });
      }
    }
    this.domainSuggestions = this.domainValues.concat();
  }

  @Input()
  public set initValue(strValue: string) {
    this._initValue = strValue;
    this.primeFromInitValue();
  }

  private primeFromInitValue(): void {
    if (this._isInitialized && this._initValue != null) {
      if (this.defn.freeFormatTxtFg) {
        this.setCustomValue(this._initValue);
        this.writeValue(this._initValue);
      } else {
        const domainValue = this.findDomainValue(this._initValue);
        if (domainValue) {
          this.setCustomValue(domainValue);
          this.writeValue(domainValue);
        } else {
          // The init value does not match a domain value but
          // we will leave it on the component as a search hint but mark as immediately invalid
          this.setCustomValue(this._initValue);
          this.writeValue(this._initValue);
          this.setError(true);
        }
      }
    }
  }

  writeValue(value: DomainValue | string): void {
    this._value = value;
  }

  public set value(v: DomainValue | string) {
    this._value = v;

    if (!this.isDisabled) {
      this.setCustomValue(v);
    }
  }

  public get value(): DomainValue | string {
    return this._value;
  }

  /**
   * Set the internal value regardless of disabled status of component. Used to ensure
   * value is set from init values or defaults
   * @param v
   */
  private setCustomValue(v: DomainValue | string) {
    if (this._isInitialized) {
      if (v == null) {
        this.myCustomValue.value = null;
      } else if (typeof v === "string") {
        if (this.defn.freeFormatTxtFg || v) {
          this.myCustomValue.value = v;
        } else if (!v) {
          this.myCustomValue.value = null;
        }
      } else if (v != null && typeof v === "object" && Object.hasOwn(v, "label")) {
        this.myCustomValue.value = !v.label ? null : v.label;
      }
    }
  }

  // /**********************************************************************/

  public getCustomValue(): CustomValue {
    return this.myCustomValue;
  }

  private findDomainValue(value: string): DomainValue {
    for (let domainValue of this.domainValues) {
      if (domainValue.label == value) {
        return domainValue;
      }
    }
    return null;
  }

  public get fieldName(): string {
    return this.defn.fieldId;
  }
  public get displayName(): string {
    return this.defn.displayName;
  }
  public get fieldType(): ATDLNControlType {
    return this._fieldType;
  }

  public searchDomain(event) {
    let query: string = event.query.toLowerCase();
    this.domainSuggestions = this.domainValues.filter(
      (a) => a.label.toLowerCase().indexOf(query) >= 0,
    );
  }

  public onSelectDomain(event) {
    this.value = event;
    this.inputFocused = false;
    this.validate();
    this.updateState();
    this.emitValueChange();
  }

  /**
   * When we lose focus, dispatch value change event on blur channel
   */
  public onInputBlur(): void {
    if (!this.isDisabled) {
      this.inputFocused = false;
      this.validate();
      this.updateState();
      this.emitValueChange();
    }
  }

  /**
   * When component gets focus set the state as active and set inputFocused flag so any tooltip is hidden
   */
  public onInputFocus(): void {
    if (!this.isDisabled) {
      this.inputFocused = true;
      this.updateState();
    }
  }

  /**
   * Validate this component value against its definition
   * @param errors input array that will be populated with error constants in case of validation error
   * @returns _true_ if component validates OK
   */
  public validate(): boolean {
    const errors: Array<string> = [];
    let isValid = true;

    this.externalState = null;

    switch (this._fieldType) {
      case ATDLNControlType.DROPDOWN_LIST:
        isValid = this.validateAutoComplete(errors);
        break;
      case ATDLNControlType.TEXTFIELD:
        isValid = this.validateTextInput(errors);
        break;
      default:
        break;
    }

    if (isValid) {
      this.errorMsg = null;
    } else {
      this.errorMsg = errors[0];
    }
    this.setError(!isValid);

    return isValid;
  }

  /**
   * Validate a field with a type of ATDLNControlType.DROPDOWN_LIST
   * @param errors
   * @returns
   */
  private validateAutoComplete(errors: Array<string>): boolean {
    let isValid = true;
    if (!this.value) {
      if (!this.defn.isNullableFg) {
        errors.push(this.translate.instant("oe.entry.custom.cannot.be.null"));
        isValid = false;
      }
    } else if (typeof this.value == "string") {
      errors.push(this.translate.instant("oe.entry.custom.searched.value.not.found"));
      isValid = false;
    } else if (this.value != null && this.value.label != null && this.value.id == null) {
      errors.push(this.translate.instant("oe.entry.custom.searched.value.not.found"));
      isValid = false;
    }
    return isValid;
  }

  /**
   * Validate a field with a type of ATDLNControlType.DROPDOWN_LIST
   * @param errors
   * @returns
   */
  private validateTextInput(errors: Array<string>): boolean {
    let isValid = true;
    if (!this.value) {
      if (!this.defn.isNullableFg) {
        isValid = false;
        errors.push(this.translate.instant("oe.entry.custom.cannot.be.null"));
      }
    } else {
      const length = (this.value as string).length;
      if (this.defn.maxLen > 0 && this.defn.maxLen < length) {
        isValid = false;
        errors.push(
          this.translate.instant("oe.entry.custom.maxlength.exceeded", {
            maxLen: this.defn.maxLen,
          }),
        );
      }
    }
    return isValid;
  }

  @Input()
  public set isDisabled(value: boolean) {
    this._isDisabled = value;
    this.updateState();
  }

  public get isDisabled(): boolean {
    return this._isDisabled || (this.defn && this.defn.disableOnPartFillFg && this.hasStrategyFill);
  }

  private updateState(): void {
    if (this.inputFocused) {
      this.state = ICNInputControlState.ACTIVE;
    } else if (this.isDisabled) {
      this.state = ICNInputControlState.INACTIVE;
    } else {
      this.state = this.hasError ? ICNInputControlState.INVALID : ICNInputControlState.VALID;
    }
  }
}
